import { BCMSCurrency, BCMSDate, Link } from "@bcm-dev/bcms-vite";
import { Autopay } from "model";
import React from "react";
import { SVGAsset } from "res";
import "./styles.css";

/**
 * @function AddedAutopayDescription
 * @param {Autopay} autopay - The autopay to use for data
 * @returns {React.Component}
 * @exported true
 * @description The main autopay description component
 */
export default function AddedAutopayDescription(props) {
    const autopay = props.autopay ?? Autopay.firstInstanceOf();
    const getShortDesc = value => value.alwaysPay === "Fixed amount"
        ? <AutopayShortFixedDescription amount={ value.fixed.format() } date={ value.deliverBy } />
        : <AutopayShortDueDescription date={ value.deliverBy } />;

    const description = autopay.alwaysPay instanceof BCMSCurrency
        ? <AutopayLongDescription amount={ autopay.alwaysPay.format() } frequency={ autopay.frequency } 
        finalDeliveryBy={ autopay.finalDeliveryBy }  duration={ autopay.duration } numberOfPayments={ autopay.numberOfPayments } />
        : getShortDesc(autopay);

    return (
        <>
            { description }

            {/*<DisabledPrompt>*/}
                <Link className="set-width" to={ `/addAutopayWorkflow/${ autopay.billerID }/2/${ autopay.id }` }>
                    <span className="flex-container flex-horizontal align-center edit-link">
                        <SVGAsset id="EditIcon" />
                        <p data-point={ `Added_Autopay_Edit_${ autopay.id }` }>Edit</p>
                    </span>
                </Link>
            {/*</DisabledPrompt>*/}
        </>
    );
}

/**
 * @function AutopayLongDescription
 * @param {STRING} amount - The string amount of the autopay
 * @param {STRING} frequency - The frequency of the autopay
 * @param {STRING} duration - The duration of the autopay
 * @returns {React.Component}
 * @exported false
 * @description The description component of a regular autopay payment
 */
function AutopayLongDescription(props) {
    const amount = props.amount ?? "[[amount]]";
    let frequency = props.frequency.toLowerCase() ?? "[[number]]";
    const numberOfPayments = props.numberOfPayments ?? "";
    const finalDeliveryBy = props.finalDeliveryBy ?? BCMSDate.init();

    let duration = props.duration !== undefined
        ? `${ props.duration.charAt(0).toLowerCase() }${ props.duration.slice(1) }`
        : "[[duration]]";
    duration = "until I stop payments" === duration ? "until I stop these payments" : duration;
    let message = `Automatically pay ${ amount } ${ frequency } ${ duration }.`;

    // Formatting differs slightly for some selected durations.
    if(duration === "for a specific number of payments") {
        message = `Automatically pay ${ amount } ${ frequency } for ${ numberOfPayments } remaining payments.`;
    } else if(duration === "until a specific date") {
        message = `Automatically pay ${ amount } ${ frequency } until, but not after ${ finalDeliveryBy.format("M d") }.`;
    }

    return <p className="promo-description gray-text flex-width">{ message }</p>
}

/**
 * @function AutopayShortDueDescription
 * @param {STRING} date - The date of the autopay
 * @returns {React.Component}
 * @exported false
 * @description The description component for an autopay that is to pay the amount due
 */
function AutopayShortDueDescription(props) {
    const date = props.date.toLowerCase() ?? "[[date]]";
    return <p className="promo-description gray-text flex-width">Automatically pay the amount due on the { date }.</p>
}

/**
 * @function AutopayShortFixedDescription
 * @param {STRING} amount - The amount of the autopay
 * @param {STRING} date - The date of the autopay
 * @returns {React.Component}
 * @exported false
 * @description The description component for an autopay that has a fixed amount
 */
function AutopayShortFixedDescription(props) {
    const amount = props.amount ?? "[[amount]]";
    const date = props.date.toLowerCase() ?? "[[date]]";

    return <p className="promo-description gray-text flex-width">Automatically pay a fixed amount of { amount }. on the { date }.</p>
}